import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, ArrowUpRight, BarChart3, Briefcase, ChevronRight, Coins,
  Globe, Loader2, LogIn, Megaphone, Rocket, Sparkles, TrendingUp, Users, Workflow, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardShell from '../components/DashboardShell';
import GettingStartedPanel from '../components/GettingStartedPanel';
import { AreaUsageChart, DonutChart, ModuleBarChart } from '../components/analytics/UsageCharts';
import { useUiTheme } from '../context/UiThemeContext';
import { apiPath } from '../lib/http';

const GROUP_ICONS = {
  Build: Rocket,
  'Marketing Suite': Megaphone,
  Grow: TrendingUp,
  AI: Sparkles,
  'AI Tools': Zap,
  Own: Globe,
  Operate: Briefcase,
};

const TONE = {
  violet: { ring: 'ring-violet-500/20', bg: 'bg-violet-500/10', text: 'text-violet-300', border: 'border-violet-500/25' },
  emerald: { ring: 'ring-emerald-500/20', bg: 'bg-emerald-500/10', text: 'text-emerald-300', border: 'border-emerald-500/25' },
  green: { ring: 'ring-green-500/20', bg: 'bg-green-500/10', text: 'text-green-300', border: 'border-green-500/25' },
  sky: { ring: 'ring-sky-500/20', bg: 'bg-sky-500/10', text: 'text-sky-300', border: 'border-sky-500/25' },
  rose: { ring: 'ring-rose-500/20', bg: 'bg-rose-500/10', text: 'text-rose-300', border: 'border-rose-500/25' },
  amber: { ring: 'ring-amber-500/20', bg: 'bg-amber-500/10', text: 'text-amber-300', border: 'border-amber-500/25' },
  purple: { ring: 'ring-purple-500/20', bg: 'bg-purple-500/10', text: 'text-purple-300', border: 'border-purple-500/25' },
  pink: { ring: 'ring-pink-500/20', bg: 'bg-pink-500/10', text: 'text-pink-300', border: 'border-pink-500/25' },
  blue: { ring: 'ring-blue-500/20', bg: 'bg-blue-500/10', text: 'text-blue-300', border: 'border-blue-500/25' },
  teal: { ring: 'ring-teal-500/20', bg: 'bg-teal-500/10', text: 'text-teal-300', border: 'border-teal-500/25' },
  cyan: { ring: 'ring-cyan-500/20', bg: 'bg-cyan-500/10', text: 'text-cyan-300', border: 'border-cyan-500/25' },
  orange: { ring: 'ring-orange-500/20', bg: 'bg-orange-500/10', text: 'text-orange-300', border: 'border-orange-500/25' },
  zinc: { ring: 'ring-zinc-500/20', bg: 'bg-zinc-500/10', text: 'text-zinc-300', border: 'border-zinc-500/25' },
};

const fmtNum = (n) => (Number(n) || 0).toLocaleString();

function TokenBuyModal({ open, onClose, packs, onPurchase, purchasing }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="platform-tokens-modal">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-zinc-950 p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Buy lead credits</h3>
            <p className="text-xs text-zinc-500 mt-1">Tokens power Lead Scraper and future AI modules.</p>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">✕</button>
        </div>
        <div className="space-y-3">
          {(packs || []).map((pack) => (
            <button
              key={pack.id}
              type="button"
              disabled={purchasing}
              onClick={() => onPurchase(pack.id)}
              className="w-full rounded-2xl border border-violet-500/30 bg-violet-500/10 p-4 text-left hover:bg-violet-500/15 transition-colors disabled:opacity-50"
              data-testid={`token-pack-${pack.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-white">{pack.label}</p>
                  <p className="text-xs text-zinc-400 mt-1">{pack.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-violet-200 font-bold">{pack.credits} tokens</p>
                  <p className="text-[11px] text-zinc-500">${pack.usd}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModuleCard({ mod, onOpen, isLight }) {
  const t = TONE[mod.tone] || TONE.zinc;
  const primary = mod.metrics?.[0];
  const accentText = isLight ? t.text.replace('-300', '-700').replace('-200', '-700') : t.text;
  return (
    <button
      type="button"
      onClick={() => mod.nav_path && onOpen(mod.nav_path)}
      className={`group text-left rounded-2xl border ${t.border} p-4 transition-all ring-1 ${t.ring} ${
        isLight ? 'bg-white hover:bg-zinc-50' : 'bg-zinc-900/50 hover:bg-zinc-900/80'
      }`}
      data-testid={`module-card-${mod.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className={`text-[10px] uppercase tracking-[0.18em] ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>{mod.group}</p>
          <h3 className={`text-sm font-semibold mt-0.5 ${isLight ? 'text-zinc-900' : 'text-white'}`}>{mod.name}</h3>
        </div>
        <ArrowUpRight className={`w-4 h-4 shrink-0 ${isLight ? 'text-zinc-500 group-hover:text-zinc-800' : 'text-zinc-600 group-hover:text-zinc-300'}`} />
      </div>
      <p className={`text-2xl font-bold ${isLight ? 'text-zinc-900' : 'text-white'}`}>{fmtNum(primary?.value ?? mod.primary_metric ?? 0)}</p>
      <p className={`text-[10px] uppercase tracking-wider mt-1 ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>{primary?.label || 'Activity'}</p>
      {mod.tokens_used > 0 ? (
        <p className={`text-[11px] mt-2 ${accentText}`}>{fmtNum(mod.tokens_used)} tokens used</p>
      ) : (
        <p className={`text-[11px] mt-2 ${isLight ? 'text-zinc-600' : 'text-zinc-600'}`}>{mod.desc?.slice(0, 60)}{(mod.desc?.length || 0) > 60 ? '…' : ''}</p>
      )}
      {mod.metrics?.length > 1 ? (
        <div className="flex flex-wrap gap-2 mt-3">
          {mod.metrics.slice(1, 4).map((m) => (
            <span key={m.label} className={`text-[10px] rounded-md px-2 py-1 ${isLight ? 'bg-zinc-100 text-zinc-700' : 'bg-black/30 text-zinc-400'}`}>
              {m.label}: {fmtNum(m.value)}
            </span>
          ))}
        </div>
      ) : null}
    </button>
  );
}

export default function PlatformOverviewDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const { isLight } = useUiTheme();
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [days, setDays] = useState(30);
  const [packs, setPacks] = useState([]);
  const [buyOpen, setBuyOpen] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState('');
  // Prefer live API result over possibly-stale user prop from an older session.
  const showAdmin = Boolean(adminData) || Boolean(user?.is_platform_admin);

  const loginAsUser = async (row) => {
    if (!token || !row?.email) return;
    if (row.id && row.id === user?.id) {
      toast.message('You are already on this account');
      return;
    }
    setImpersonatingId(row.id || row.email);
    try {
      const res = await fetch(apiPath('/auth/admin/impersonate'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: row.email, user_id: row.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Could not open user dashboard');
      }
      // Keep admin session so "Exit to admin" can restore it.
      localStorage.setItem('admin_token_backup', token);
      localStorage.setItem('admin_impersonator_email', user?.email || '');
      localStorage.setItem('token', body.token);
      if (typeof setUser === 'function') setUser(body.user);
      toast.success(`Viewing dashboard as ${row.email}`);
      navigate('/dashboard');
      window.location.reload();
    } catch (err) {
      toast.error(err.message || 'Impersonation failed');
    } finally {
      setImpersonatingId('');
    }
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [dashRes, catalogRes, adminRes] = await Promise.all([
        fetch(apiPath(`/platform-analytics/dashboard?days=${days}`), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(apiPath('/lead-credits/catalog')),
        fetch(apiPath(`/platform-analytics/admin?days=${days}`), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const dash = await dashRes.json().catch(() => ({}));
      const catalog = await catalogRes.json().catch(() => ({}));
      if (!dashRes.ok) throw new Error(dash.detail || `HTTP ${dashRes.status}`);
      setData(dash);
      setPacks(Array.isArray(catalog.packs) ? catalog.packs : []);
      if (adminRes.ok) {
        const admin = await adminRes.json().catch(() => null);
        setAdminData(admin);
      } else {
        setAdminData(null);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load platform analytics');
    } finally {
      setLoading(false);
    }
  }, [days, token]);

  useEffect(() => { load(); }, [load]);

  const purchasePack = async (packId) => {
    setPurchasing(true);
    try {
      const res = await fetch(apiPath('/lead-credits/checkout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          pack_id: packId,
          success_url: `${window.location.origin}/dashboard?tokens_payment=stripe&session_id={SESSION_ID}`,
          cancel_url: `${window.location.origin}/dashboard?tokens_payment=cancelled`,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail?.message || body.detail || 'Checkout failed');
      if (body.checkout_url) {
        window.location.href = body.checkout_url;
        return;
      }
      if (body.mode === 'mock' && body.session_id) {
        const confirm = await fetch(apiPath(`/lead-credits/checkout/${body.session_id}/confirm-mock`), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        const confirmed = await confirm.json().catch(() => ({}));
        if (!confirm.ok) throw new Error(confirmed.detail || 'Mock payment failed');
        toast.success(confirmed.message || 'Tokens added');
        setBuyOpen(false);
        load();
      }
    } catch (err) {
      toast.error(err.message || 'Could not start checkout');
    } finally {
      setPurchasing(false);
    }
  };

  const donutSegments = useMemo(() => {
    const rows = data?.usage?.by_module || [];
    return rows.slice(0, 6).map((r) => ({ label: r.name, value: r.tokens }));
  }, [data]);

  const groupedModules = useMemo(() => data?.groups || {}, [data]);

  const headerExtras = data?.tokens?.enabled ? (
    <div
      className={`flex items-center gap-2 rounded-xl border px-3 py-1.5 ${
        isLight ? 'border-violet-300 bg-violet-50' : 'border-violet-500/30 bg-violet-500/10'
      }`}
      data-testid="platform-token-balance"
    >
      <Coins className={`w-4 h-4 ${isLight ? 'text-violet-700' : 'text-violet-300'}`} />
      <span className={`text-xs ${isLight ? 'text-zinc-600' : 'text-zinc-400'}`}>Tokens</span>
      <span className={`text-sm font-bold ${isLight ? 'text-zinc-900' : 'text-white'}`}>{fmtNum(data.tokens.balance)}</span>
      <button
        type="button"
        onClick={() => setBuyOpen(true)}
        className={`ml-1 rounded-lg px-2 py-0.5 text-[10px] font-medium ${
          isLight
            ? 'bg-violet-600 text-white hover:bg-violet-700'
            : 'bg-violet-500/20 text-violet-200 hover:bg-violet-500/30'
        }`}
        data-testid="platform-buy-tokens"
      >
        Buy
      </button>
    </div>
  ) : null;

  return (
    <DashboardShell
      user={user}
      title="Platform Overview"
      subtitle="Analytics, token usage, and module activity across Snagcart"
      activeNav="overview"
      headerExtras={headerExtras}
    >
      <div className="p-4 md:p-6 lg:p-8 space-y-6 max-w-[1400px] mx-auto" data-testid="platform-overview-dashboard">
        <GettingStartedPanel dashboardData={data} />
        {loading && !data ? (
          <div className="flex items-center justify-center py-24 text-zinc-400">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading analytics…
          </div>
        ) : (
          <>
            {showAdmin && adminData ? (
              <section
                className={`rounded-3xl border p-5 space-y-4 ${
                  isLight
                    ? 'border-amber-300 bg-amber-50/80'
                    : 'border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-zinc-900/80 to-zinc-950'
                }`}
                data-testid="platform-admin-analytics"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className={`text-[10px] uppercase tracking-[0.2em] ${isLight ? 'text-amber-700' : 'text-amber-300/80'}`}>Superadmin</p>
                    <h2 className={`text-sm font-semibold mt-0.5 ${isLight ? 'text-zinc-900' : 'text-white'}`}>Platform registrations &amp; plans</h2>
                    <p className={`text-xs ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>SaaS-wide metrics visible only to platform admins</p>
                  </div>
                  <div className={`flex items-center gap-2 text-xs ${isLight ? 'text-zinc-600' : 'text-zinc-400'}`}>
                    <Users className={`w-4 h-4 ${isLight ? 'text-amber-700' : 'text-amber-300'}`} />
                    Last {adminData.period_days || days} days
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { label: 'Total users', value: adminData.summary?.total_users, sub: `${fmtNum(adminData.summary?.deleted_users)} deleted`, tone: 'amber' },
                    { label: 'New signups', value: adminData.summary?.new_signups, sub: `Last ${days} days`, tone: 'emerald' },
                    { label: 'Paid users', value: adminData.summary?.paid_users, sub: `${fmtNum(adminData.summary?.free_users)} on Basic`, tone: 'violet' },
                    { label: 'Websites', value: adminData.summary?.websites_total, sub: `${fmtNum(adminData.summary?.websites_created_period)} new in period`, tone: 'sky' },
                  ].map(({ label, value, sub, tone }) => {
                    const t = TONE[tone] || TONE.amber;
                    return (
                      <div key={label} className={`rounded-2xl border ${t.border} ${t.bg} p-4 ${isLight ? 'bg-white' : ''}`} data-testid={`admin-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                        <p className={`text-[10px] uppercase tracking-wider ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>{label}</p>
                        <p className={`text-3xl font-bold mt-1 ${isLight ? 'text-zinc-900' : 'text-white'}`}>{fmtNum(value)}</p>
                        <p className={`text-[11px] mt-1 ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>{sub}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  <div className={`xl:col-span-2 rounded-2xl border p-4 ${isLight ? 'border-zinc-200 bg-white' : 'border-white/10 bg-black/20'}`}>
                    <h3 className={`text-xs font-semibold mb-1 ${isLight ? 'text-zinc-900' : 'text-white'}`}>Signups over time</h3>
                    <p className={`text-[11px] mb-3 ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>New account registrations per day</p>
                    <AreaUsageChart
                      data={(adminData.signups_by_day || []).map((row) => ({
                        date: row.date,
                        tokens: row.signups,
                      }))}
                      color="#fbbf24"
                      testId="admin-signups-area"
                    />
                  </div>
                  <div className={`rounded-2xl border p-4 space-y-3 ${isLight ? 'border-zinc-200 bg-white' : 'border-white/10 bg-black/20'}`}>
                    <h3 className={`text-xs font-semibold ${isLight ? 'text-zinc-900' : 'text-white'}`}>Plan mix</h3>
                    {[
                      { key: 'basic', label: 'Basic (free)', tone: 'zinc' },
                      { key: 'starter', label: 'Starter', tone: 'violet' },
                      { key: 'growth', label: 'Growth', tone: 'emerald' },
                    ].map(({ key, label, tone }) => {
                      const t = TONE[tone] || TONE.zinc;
                      const count = adminData.plans?.[key] || 0;
                      const total = Math.max(1, adminData.summary?.total_users || 1);
                      const pct = Math.round((count / total) * 100);
                      return (
                        <div key={key}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className={isLight ? 'text-zinc-700' : 'text-zinc-400'}>{label}</span>
                            <span className={`font-medium ${isLight ? 'text-zinc-900' : 'text-white'}`}>{fmtNum(count)} · {pct}%</span>
                          </div>
                          <div className={`h-2 rounded-full overflow-hidden ${isLight ? 'bg-zinc-200' : 'bg-zinc-800'}`}>
                            <div className={`h-full ${t.bg.replace('/10', '/60')}`} style={{ width: `${pct}%`, backgroundColor: tone === 'violet' ? '#8b5cf6' : tone === 'emerald' ? '#34d399' : '#71717a' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className={`rounded-2xl border overflow-hidden ${isLight ? 'border-zinc-200 bg-white' : 'border-white/10 bg-black/20'}`}>
                  <div className={`px-4 py-3 flex items-center justify-between gap-2 ${isLight ? 'border-b border-zinc-200' : 'border-b border-white/5'}`}>
                    <h3 className={`text-xs font-semibold ${isLight ? 'text-zinc-900' : 'text-white'}`}>All users</h3>
                    <span className={`text-[10px] ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>{fmtNum((adminData.users || adminData.recent_signups || []).length)} shown</span>
                  </div>
                  {!(adminData.users || adminData.recent_signups || []).length ? (
                    <p className={`text-sm py-6 text-center ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>No users yet.</p>
                  ) : (
                    <div className="overflow-x-auto max-h-96 overflow-y-auto">
                      <table className="w-full text-left text-sm" data-testid="admin-users-table">
                        <thead className={`sticky top-0 text-[10px] uppercase tracking-wider ${isLight ? 'bg-zinc-100 text-zinc-600' : 'bg-zinc-950/95 text-zinc-500'}`}>
                          <tr>
                            <th className="px-4 py-2 font-medium">Name</th>
                            <th className="px-4 py-2 font-medium">Email</th>
                            <th className="px-4 py-2 font-medium">Country</th>
                            <th className="px-4 py-2 font-medium">Plan</th>
                            <th className="px-4 py-2 font-medium">Joined</th>
                            <th className="px-4 py-2 font-medium text-right">Dashboard</th>
                          </tr>
                        </thead>
                        <tbody className={isLight ? 'divide-y divide-zinc-200' : 'divide-y divide-white/5'}>
                          {(adminData.users || adminData.recent_signups || []).map((row) => (
                            <tr key={row.id || row.email} className={isLight ? 'hover:bg-zinc-50' : 'hover:bg-white/[0.02]'}>
                              <td className={`px-4 py-2.5 max-w-[140px] truncate ${isLight ? 'text-zinc-900' : 'text-zinc-200'}`}>{row.name || '—'}</td>
                              <td className={`px-4 py-2.5 max-w-[200px] truncate ${isLight ? 'text-zinc-700' : 'text-zinc-400'}`}>{row.email}</td>
                              <td className={`px-4 py-2.5 ${isLight ? 'text-zinc-700' : 'text-zinc-300'}`}>{row.country || '—'}</td>
                              <td className="px-4 py-2.5">
                                <span className={`rounded-md px-2 py-0.5 text-[11px] capitalize ${isLight ? 'bg-amber-100 text-amber-800' : 'bg-amber-500/10 text-amber-200'}`}>
                                  {row.whitelabel_plan || row.plan || 'basic'}
                                </span>
                              </td>
                              <td className={`px-4 py-2.5 text-[11px] whitespace-nowrap ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>
                                {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <button
                                  type="button"
                                  onClick={() => loginAsUser(row)}
                                  disabled={Boolean(impersonatingId) || row.id === user?.id}
                                  className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50 ${
                                    isLight
                                      ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
                                      : 'border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20'
                                  }`}
                                  data-testid={`admin-login-as-${row.id || row.email}`}
                                  title="Open this user's dashboard as admin"
                                >
                                  {(impersonatingId === row.id || impersonatingId === row.email)
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : <LogIn className="w-3 h-3" />}
                                  Login as
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
            ) : null}

            {/* Hero KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Token balance', value: data?.tokens?.balance, sub: `${fmtNum(data?.tokens?.lifetime_used)} lifetime used`, tone: 'violet', icon: Coins },
                { label: 'Tokens used', value: data?.summary?.tokens_used_period, sub: `Last ${days} days`, tone: 'rose', icon: Activity },
                { label: 'Websites', value: data?.summary?.websites, sub: 'Published & draft', tone: 'emerald', icon: Globe },
                { label: 'Active modules', value: data?.summary?.active_modules, sub: 'With usage or data', tone: 'sky', icon: BarChart3 },
              ].map(({ label, value, sub, tone, icon: Icon }) => {
                const t = TONE[tone] || TONE.violet;
                return (
                  <div key={label} className={`rounded-2xl border ${t.border} ${isLight ? 'bg-white' : t.bg} p-4 ring-1 ${t.ring}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-4 h-4 ${isLight ? t.text.replace('-300', '-700') : t.text}`} />
                      <span className={`text-[10px] uppercase tracking-wider ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>{label}</span>
                    </div>
                    <p className={`text-3xl font-bold ${isLight ? 'text-zinc-900' : 'text-white'}`}>{fmtNum(value)}</p>
                    <p className={`text-[11px] mt-1 ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>{sub}</p>
                  </div>
                );
              })}
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div
                className={`xl:col-span-2 rounded-3xl border p-5 ${
                  isLight ? 'border-zinc-200 bg-white' : 'border-white/10 bg-gradient-to-br from-violet-500/5 via-zinc-900/80 to-zinc-950'
                }`}
                data-testid="token-usage-chart-card"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className={`text-sm font-semibold ${isLight ? 'text-zinc-900' : 'text-white'}`}>Token usage over time</h2>
                    <p className={`text-xs ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>Daily debits across all modules</p>
                  </div>
                  <select
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                    className={`h-8 rounded-lg border px-2 text-xs ${
                      isLight ? 'bg-white border-zinc-300 text-zinc-900' : 'bg-zinc-950 border-zinc-700 text-white'
                    }`}
                    data-testid="analytics-period-select"
                  >
                    <option value={7}>7 days</option>
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                  </select>
                </div>
                <AreaUsageChart data={data?.usage?.by_day || []} color="#a78bfa" testId="platform-token-area" />
              </div>

              <div
                className={`rounded-3xl border p-5 space-y-4 ${isLight ? 'border-zinc-200 bg-white' : 'border-white/10 bg-zinc-900/60'}`}
                data-testid="module-breakdown-card"
              >
                <div>
                  <h2 className={`text-sm font-semibold ${isLight ? 'text-zinc-900' : 'text-white'}`}>Usage by module</h2>
                  <p className={`text-xs ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>Token spend breakdown</p>
                </div>
                <div className="flex items-center gap-4">
                  <DonutChart segments={donutSegments} testId="module-donut" />
                  <div className="flex-1 min-w-0 space-y-1">
                    {donutSegments.map((s) => (
                      <div key={s.label} className="flex justify-between text-[11px]">
                        <span className={`truncate pr-2 ${isLight ? 'text-zinc-700' : 'text-zinc-400'}`}>{s.label}</span>
                        <span className={`shrink-0 ${isLight ? 'text-zinc-900' : 'text-zinc-300'}`}>{s.value}</span>
                      </div>
                    ))}
                    {!donutSegments.length ? (
                      <p className={`text-xs ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>Scrape leads or use tokenized modules to see breakdown.</p>
                    ) : null}
                  </div>
                </div>
                <ModuleBarChart items={data?.usage?.by_module || []} testId="module-bar-chart" />
              </div>
            </div>

            {/* Module groups */}
            {Object.entries(groupedModules).map(([group, mods]) => {
              const Icon = GROUP_ICONS[group] || Workflow;
              return (
                <section key={group} className="space-y-3" data-testid={`module-group-${group.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${isLight ? 'text-zinc-600' : 'text-zinc-400'}`} />
                    <h2 className={`text-sm font-semibold ${isLight ? 'text-zinc-900' : 'text-white'}`}>{group}</h2>
                    <span className={`text-[10px] ${isLight ? 'text-zinc-600' : 'text-zinc-600'}`}>{mods.length} modules</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                    {mods.map((mod) => (
                      <ModuleCard key={mod.id} mod={mod} onOpen={navigate} isLight={isLight} />
                    ))}
                  </div>
                </section>
              );
            })}

            {/* Recent token activity */}
            <section
              className={`rounded-3xl border p-5 ${isLight ? 'border-zinc-200 bg-white' : 'border-white/10 bg-zinc-900/50'}`}
              data-testid="recent-token-activity"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className={`text-sm font-semibold ${isLight ? 'text-zinc-900' : 'text-white'}`}>Recent token activity</h2>
                  <p className={`text-xs ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>Credits and debits from the platform ledger</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/lead-scraper')}
                  className={`text-xs inline-flex items-center gap-1 ${isLight ? 'text-violet-700 hover:text-violet-900' : 'text-violet-300 hover:text-violet-200'}`}
                >
                  Lead Scraper <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              {!data?.recent_activity?.length ? (
                <p className={`text-sm py-6 text-center ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>No token activity yet. Lead Scraper uses 1 token per lead scraped.</p>
              ) : (
                <div className={isLight ? 'divide-y divide-zinc-200' : 'divide-y divide-white/5'}>
                  {data.recent_activity.map((row) => (
                    <div key={row.id} className="flex items-center justify-between py-3 text-sm">
                      <div>
                        <p className={isLight ? 'text-zinc-900' : 'text-zinc-200'}>{row.label}</p>
                        <p className={`text-[11px] ${isLight ? 'text-zinc-600' : 'text-zinc-500'}`}>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${row.direction === 'debit' ? (isLight ? 'text-rose-700' : 'text-rose-300') : (isLight ? 'text-emerald-700' : 'text-emerald-300')}`}>
                          {row.direction === 'debit' ? '-' : '+'}{row.tokens}
                        </p>
                        <p className={`text-[10px] ${isLight ? 'text-zinc-600' : 'text-zinc-600'}`}>bal {row.balance_after ?? '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Quick links */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: '✨ AI Co-founder', path: '/ai-cofounder', tone: 'amber' },
                { label: 'My Websites', path: '/websites', tone: 'violet' },
                { label: 'Marketing Suite', path: '/marketing-suite', tone: 'pink' },
                { label: 'Lead Scraper', path: '/lead-scraper', tone: 'rose' },
                { label: 'Snag Builder', path: '/snagbuilder', tone: 'emerald' },
              ].map(({ label, path, tone }) => {
                const t = TONE[tone] || TONE.zinc;
                return (
                  <button
                    key={path}
                    type="button"
                    data-testid={`quicklink-${path.replace('/', '')}`}
                    onClick={() => navigate(path)}
                    className={`rounded-2xl border ${t.border} ${t.bg} px-4 py-3 text-left text-sm font-medium hover:brightness-110 transition-all ${
                      isLight ? 'text-zinc-900' : 'text-white'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <TokenBuyModal
        open={buyOpen}
        onClose={() => setBuyOpen(false)}
        packs={packs}
        onPurchase={purchasePack}
        purchasing={purchasing}
      />
    </DashboardShell>
  );
}
